import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '@/lib/utils';

function Card({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card"
      className={cn(
        // SL-0027, Activity 4, Task 4.2: the mockup's own `.card` padding (16px, confirmed
        // against `docs/mockups/organization-ui/index.html` at execution time) replaces the
        // scaffold's `py-6`/`rounded-xl`; border/background/text already matched the token layer.
        // Flat by default — a static card carries a border and NO resting shadow (Level 0 of the
        // Brand Kit's elevation scale, docs/guides/brand-kit.md § Elevation And Shadows: "static
        // cards use a border instead of a shadow"). Shadow is reserved for floating/hover surfaces.
        'flex flex-col gap-6 rounded-md border border-border bg-card p-4 text-card-foreground',
        className,
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
        className,
      )}
      {...props}
    />
  );
}

// `asChild` lets a screen pass its own heading element (e.g. `<h1>`) while keeping the CardTitle
// styling — the default `<div>` carries no heading semantics, so a title that is visually the page
// heading (login, blocking policy screen) would otherwise expose no programmatic heading (WCAG 2.2
// SC 1.3.1). Existing call sites that omit `asChild` are unchanged (still a styled `<div>`).
function CardTitle({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'div';
  return (
    <Comp
      data-slot="card-title"
      className={cn('text-card-foreground leading-none font-semibold', className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn('px-6', className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 [.border-t]:pt-6', className)}
      {...props}
    />
  );
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent };
